import { useEffect, useRef } from 'react';
import { Permission } from '@bookora/shared';
import { useQueryClient } from '@tanstack/react-query';

import type { OnboardingStateResponse } from '@/api/generated/model';
import { useAuth } from '@/auth/token-store';
import { usePlanAccess } from '@/features/plan/plan-access';
import { clinicApi } from '@/features/settings/api';
import { settingsKeys, useOnboarding } from '@/features/settings/hooks';
import { useCan } from '@/permissions/use-can';

import { visibleSteps, type WizardStep } from './setup-steps';

/**
 * «بعداً ادامه می‌دم» — for the rest of this browser session the owner is not
 * sent back to the wizard on `/`. Per clinic: someone who set up one clinic
 * and opened another is still walked through the second. Session storage on
 * purpose: tomorrow's login asks again, because an unfinished setup is a
 * clinic whose calendar offers no slot.
 */
const LATER_KEY = (clinicId: string) => `bk.setup.later.${clinicId}`;

export function postponeSetup(clinicId: string | null): void {
  if (!clinicId) return;
  try {
    sessionStorage.setItem(LATER_KEY(clinicId), '1');
  } catch {
    // Private mode / blocked storage: the redirect simply asks again.
  }
}

export function isSetupPostponed(clinicId: string | null): boolean {
  if (!clinicId) return false;
  try {
    return sessionStorage.getItem(LATER_KEY(clinicId)) === '1';
  } catch {
    return false;
  }
}

/** The steps this clinic walks, with the stock step only when the plan has `inventory`. */
export function useWizardSteps(
  onboarding: OnboardingStateResponse | undefined,
): readonly WizardStep[] {
  const plan = usePlanAccess();
  // Fails open like every plan read: while unknown, the step is shown — it is
  // optional, so showing it costs a click and hiding it wrongly costs a feature.
  const hasInventory = plan.data ? plan.data.features.inventory !== false : true;
  return visibleSteps(onboarding, hasInventory);
}

/**
 * Marks setup complete the moment every required step is done — wherever they
 * were done: in the wizard, or on the feature pages themselves. Completion
 * used to be a separate «تکمیل راه‌اندازی» click behind a confirm dialog, and
 * until somebody found it the dashboard kept nagging an owner whose clinic was
 * already taking bookings.
 *
 * The server refuses while a required step is open and keeps the first stamp
 * on a repeat, so the call is safe to make whenever `canComplete` flips. Never
 * while impersonating (support looking at a clinic must not finish its setup)
 * and only for someone who may edit settings.
 */
export function useAutoCompleteSetup(): void {
  const { activeClinicId, impersonatedBy } = useAuth();
  const canEdit = useCan(Permission.SETTINGS_EDIT);
  const onboarding = useOnboarding();
  const queryClient = useQueryClient();
  const sent = useRef<string | null>(null);

  const ready =
    canEdit &&
    !impersonatedBy &&
    Boolean(onboarding.data?.canComplete) &&
    !onboarding.data?.completedAt &&
    activeClinicId !== null;

  useEffect(() => {
    if (!ready || sent.current === activeClinicId) return;
    sent.current = activeClinicId;
    void clinicApi
      .completeOnboarding()
      .then(() =>
        queryClient.invalidateQueries({ queryKey: settingsKeys.onboarding(activeClinicId) }),
      )
      .catch(() => {
        // A refused call (a step reopened in between) is retried on the next flip.
        sent.current = null;
      });
  }, [ready, activeClinicId, queryClient]);
}
